import type { FigmaFilesData, FigmaFilesAPIData } from "@/src/types/index.ts";
import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { FigmaRestApi } from "@zeytal/types";

import { wait } from "@endgame/utils/wait";
import { findLatestFileVersion, getFigmaFileEndpoint } from "@zeytal/figma";
import { cyan } from "colorette";

import { logger } from "@/src/utils/logger.ts";

export const figmaAPICallsDelay = 0;
export const figmaAWSAssetsCallsDelay = 150;

function batchIds(nodesArray: FigmaRestApi.CanvasNode[], batchSize: number) {
  const result = [];

  for (let i = 0; i < nodesArray.length; i += batchSize) {
    const batch = nodesArray
      .slice(i, i + batchSize)
      .map((obj) => obj.id)
      .join(",");
    result.push(batch);
  }

  return result;
}

export const fetchFilesVersions = async (
  figmaFiles: FigmaFilesAPIData["figmaFiles"],
  figmaApiKey: string,
  zeytalConfig: ZeytalCLIPureConfig,
  category: "assets" | "styles",
) => {
  const filesWithVersion = [];

  for (const { fileId } of figmaFiles) {
    if (!fileId || !figmaApiKey) continue;

    if (filesWithVersion.length > 0) {
      // delay between requests
      await wait(figmaAPICallsDelay);
    }

    const fileVersion = await findLatestFileVersion(
      figmaApiKey,
      fileId,
      zeytalConfig[category].figmaFilesVersion,
    );

    if (fileVersion) {
      filesWithVersion.push(fileVersion);
    }
  }

  return filesWithVersion;
};

export const fetchFilesData = async (
  figmaFilesPagesBatchSize: number,
  filesWithVersion: NonNullable<Awaited<ReturnType<typeof fetchFilesVersions>>>,
  figmaApiKey: string,
) => {
  const figmaFilesData: FigmaFilesData = [];

  // Get the pages in the Figma files that are not empty
  for (const { id: fileId, version } of filesWithVersion) {
    const versionQueryParam = version ? `version=${version}` : "";
    const depth1QueryParam = "depth=1";
    const filePagesQueryParams = versionQueryParam
      ? `${versionQueryParam}&${depth1QueryParam}`
      : depth1QueryParam;

    logger.resetSpinner();
    logger.spinner().start(`Fetching file...`);

    // Step 1: Get all file's pages
    const filePages = await getFigmaFileEndpoint(
      figmaApiKey,
      fileId,
      filePagesQueryParams,
    );
    let { children: pagesList } = filePages.document;

    // Ignoring Figma divider pages
    pagesList = pagesList.filter(({ name: pageName }) => pageName !== "---");

    const pagesListIds = batchIds(pagesList, figmaFilesPagesBatchSize);
    const pagesListIdsLength = pagesListIds.length;
    let loopIndex: number;

    // Reset file childrens (Figma pages)
    filePages.document.children = [];
    filePages.styles = {};

    logger.spinner().succeed(`${cyan(filePages.name)} file fetched.`);

    // Step 2: Process each page sequentially with a delay
    for (loopIndex = 0; loopIndex < pagesListIdsLength; loopIndex++) {
      const pageIds = pagesListIds[loopIndex];
      if (!pageIds) {
        continue;
      }

      logger
        .spinner()
        .start(
          `Getting file pages data — Batch ${loopIndex + 1}/${pagesListIdsLength}`,
        );

      // Make the API calls for the current page and store the result
      const pageIdQueryParam = "ids=" + pageIds;
      const pageQueryParams = versionQueryParam
        ? `${versionQueryParam}&${pageIdQueryParam}`
        : pageIdQueryParam;

      const { document, styles } = await getFigmaFileEndpoint(
        figmaApiKey,
        fileId,
        pageQueryParams,
      );

      // filter out irrelevant empty file pages
      const pagesData = document.children.filter(({ id }) =>
        pageIds.includes(id),
      );

      if (pagesData.length <= 0) {
        continue;
      }

      filePages.document.children.push(...pagesData);
      filePages.styles = { ...filePages.styles, ...styles };

      // Add a delay after the API call, if figmaAPICallsDelay is positive
      if (figmaAPICallsDelay >= 0) {
        await wait(figmaAPICallsDelay);
      }

      logger.spinner().succeed();
    }

    figmaFilesData.push({
      fileId,
      document: filePages.document,
      styles: filePages.styles,
    });

    logger.spinner().succeed(`${cyan(filePages.name)} pages data fetched.`);
  }

  return figmaFilesData;
};
